import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CommandBar } from '../components/CommandBar';
import { ContextMenu } from '../components/ContextMenu';
import { ListFilters } from '../components/ListFilters';
import { MoveScopeDialog } from '../components/MoveScopeDialog';
import { NewTaskModal } from '../components/NewTaskModal';
import { TaskDetail } from '../components/TaskDetail';
import { TaskList } from '../components/TaskList';
import { ViewHeader } from '../components/ViewHeader';
import { ViewerToggle } from '../components/ViewerToggle';
import { useUndo } from '../context/UndoContext';
import { useAuth } from '../context/AuthContext';
import { useMe } from '../hooks/useMe';
import { useUserTeams } from '../hooks/useUserTeams';
import { useTaskContextMenu } from '../hooks/useTaskContextMenu';
import { usePersistedState } from '../hooks/usePersistedState';
import { filterByViewer } from '../utils/viewerFilter';
import { setTaskCountsCache } from '../hooks/useTaskCounts';
import { useTaskKeyboard } from '../hooks/useTaskKeyboard';
import { useTaskPolling } from '../hooks/useTaskPolling';
import { searchTasks } from '../utils/searchTasks';
import type { Task, TaskPriority } from '../types';
import { sortTasks, type SortMode } from '../utils/sort';
import * as api from '../utils/apiClient';

function isInbox(task: Task): boolean {
  return task.status !== 'completed';
}

export function InboxView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedId = searchParams.get('t');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [query, setQuery] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const [transitioningIds, setTransitioningIds] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const { me } = useMe();
  const { userTeams } = useUserTeams();
  const [viewer, setViewer] = usePersistedState<string>('viewer.selected', 'self');
  const [sortMode, setSortMode] = usePersistedState<SortMode>('sort.inbox', 'smart');
  const undo = useUndo();
  const ctx = useTaskContextMenu();

  const refresh = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const ts = await api.listTasks('combined');
      setTasks(ts);
      setError(null);
      // Sidebar reads the same combined payload — populate its cache directly
      // so it isn't gated on its own (auth-race-prone) fetch path.
      setTaskCountsCache(ts);
    } catch (err) {
      if (showSpinner) setError((err as Error).message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  useTaskPolling(() => {
    void refresh();
  });

  const viewerFiltered = useMemo(
    () => filterByViewer(tasks, viewer, { mySub: me?.sub ?? user?.userId ?? null, userTeams }),
    [tasks, viewer, me, user, userTeams]
  );

  const inboxTasks = useMemo(
    () => viewerFiltered.filter((t) => isInbox(t) || transitioningIds.has(t.taskId)),
    [viewerFiltered, transitioningIds]
  );
  const activeCount = useMemo(
    () => viewerFiltered.filter((t) => t.status !== 'completed').length,
    [viewerFiltered]
  );
  const filtered = useMemo(() => searchTasks(inboxTasks, query), [inboxTasks, query]);
  const sorted = useMemo(() => sortTasks(filtered, sortMode), [filtered, sortMode]);
  const selected = sorted.find((t) => t.taskId === selectedId) ?? null;
  const selectedIdx = selected ? sorted.findIndex((t) => t.taskId === selected.taskId) : -1;

  const select = useCallback(
    (taskId: string | null) => {
      const wasSelected = searchParams.has('t');
      const willSelect = !!taskId;
      if (wasSelected && !willSelect) {
        // Clearing — pop history so iOS back-swipe + in-app back are consistent
        navigate(-1);
        return;
      }
      const next = new URLSearchParams(searchParams);
      if (taskId) next.set('t', taskId);
      else next.delete('t');
      // First selection pushes history (enables iOS back-swipe). Switching tasks replaces.
      const replace = wasSelected && willSelect;
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams, navigate]
  );

  const upsertTask = useCallback((task: Task) => {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.taskId === task.taskId);
      if (idx < 0) return [...prev, task];
      const next = [...prev];
      next[idx] = task;
      return next;
    });
  }, []);

  const removeLocalTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
  }, []);

  const onUpdate = useCallback(
    async (patch: Parameters<typeof api.updateTask>[2]) => {
      if (!selected) return;
      const updated = await api.updateTask(selected.teamId, selected.taskId, patch);
      upsertTask(updated);
    },
    [selected, upsertTask]
  );

  const onMove = useCallback(
    async (toTeamId: string) => {
      if (!selected) return;
      await api.moveTask(selected.teamId, selected.taskId, toTeamId);
      removeLocalTask(selected.taskId);
      select(null);
    },
    [selected, removeLocalTask, select]
  );

  const deleteWithUndo = useCallback(
    (task: Task) => {
      removeLocalTask(task.taskId);
      if (selected?.taskId === task.taskId) select(null);
      undo.queue({
        message: `Deleted: ${task.title}`,
        reverse: () => {
          upsertTask(task);
        },
        onExpire: async () => {
          try {
            await api.deleteTask(task.teamId, task.taskId);
          } catch {
            // best-effort; refresh will reconcile
          }
        },
      });
    },
    [removeLocalTask, selected, select, undo, upsertTask]
  );

  const onDelete = useCallback(() => {
    if (!selected) return;
    deleteWithUndo(selected);
  }, [selected, deleteWithUndo]);

  const onAddNote = useCallback(
    async (body: string) => {
      if (!selected) return;
      const updated = await api.addNote(selected.teamId, selected.taskId, body);
      upsertTask(updated);
    },
    [selected, upsertTask]
  );

  const completeTaskWithUndo = useCallback(
    (original: Task) => {
      if (original.status === 'completed') return;
      upsertTask({ ...original, status: 'completed', completedAt: new Date().toISOString() });
      setTransitioningIds((prev) => new Set(prev).add(original.taskId));
      setTimeout(() => {
        setTransitioningIds((prev) => {
          const next = new Set(prev);
          next.delete(original.taskId);
          return next;
        });
      }, 600);
      undo.queue({
        message: `Completed: ${original.title}`,
        reverse: async () => {
          upsertTask(original);
          await api.updateTask(original.teamId, original.taskId, { status: original.status });
        },
      });
      void api.updateTask(original.teamId, original.taskId, { status: 'completed' }).catch(() => {});
    },
    [upsertTask, undo]
  );

  const completeWithUndo = useCallback(() => {
    if (!selected) return;
    completeTaskWithUndo(selected);
  }, [selected, completeTaskWithUndo]);

  const createTask = useCallback(
    async (input: {
      teamId: string;
      title: string;
      priority?: TaskPriority;
      dueDate?: string;
      note?: string;
      assigneeId?: string;
      private?: boolean;
    }) => {
      const { note, ...taskInput } = input;
      // Manual create defaults to self; explicit assigneeId from the modal wins.
      const assigneeId = taskInput.assigneeId ?? user?.userId;
      const created = await api.createTask({ ...taskInput, assigneeId });
      const final = note ? await api.addNote(created.teamId, created.taskId, note) : created;
      upsertTask(final);
      select(final.taskId);
    },
    [upsertTask, select, user]
  );

  const snooze = useCallback(async () => {
    if (!selected) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const updated = await api.updateTask(selected.teamId, selected.taskId, {
      dueDate: tomorrow.toISOString(),
    });
    upsertTask(updated);
  }, [selected, upsertTask]);

  useTaskKeyboard(
    {
      next: () => {
        if (sorted.length === 0) return;
        const nextIdx = Math.min(sorted.length - 1, selectedIdx < 0 ? 0 : selectedIdx + 1);
        select(sorted[nextIdx].taskId);
      },
      prev: () => {
        if (sorted.length === 0) return;
        const prevIdx = Math.max(0, selectedIdx < 0 ? 0 : selectedIdx - 1);
        select(sorted[prevIdx].taskId);
      },
      close: () => select(null),
      complete: completeWithUndo,
      togglePin: () => void onUpdate({ pinned: !selected?.pinned }),
      snooze: () => void snooze(),
      addNote: () => {
        const textarea = document.querySelector<HTMLTextAreaElement>('.note-composer textarea');
        textarea?.focus();
      },
      setPriority: (priority: TaskPriority) => void onUpdate({ priority }),
      move: (toTeamId: string) => void onMove(toTeamId),
      toggleHelp: () => setShowHelp((v) => !v),
    },
    !showHelp
  );

  return (
    <div className="scope-view">
      <ViewHeader
        title="Combined"
        searchValue={query}
        onSearchChange={setQuery}
        onNewTask={() => setShowNewTask(true)}
        onShowShortcuts={() => setShowHelp(true)}
        leadingSlot={<ViewerToggle value={viewer} onChange={setViewer} />}
      />

      <div className={`pane-wrap ${selected ? 'has-selection' : ''}`}>
        <section className="list-pane" aria-label="Tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <ListFilters
                activeCount={activeCount}
                sortMode={sortMode}
                onSortChange={(m) => setSortMode(m as SortMode)}
                viewer={viewer}
                onViewerChange={setViewer}
              />
              <TaskList
                tasks={sorted}
                selectedTaskId={selected?.taskId ?? null}
                onSelect={select}
                onContextMenu={ctx.openContextMenu}
                onSwipeComplete={completeTaskWithUndo}
                onSwipeDelete={deleteWithUndo}
                emptyMessage={query ? 'No tasks match your search.' : "You're caught up 🎉"}
              />
            </>
          )}
        </section>

        <section className="detail-pane" aria-label="Task detail">
          {selected ? (
            <TaskDetail
              key={selected.taskId}
              task={selected}
              onUpdate={onUpdate}
              onComplete={completeWithUndo}
              onMove={onMove}
              onDelete={onDelete}
              onAddNote={onAddNote}
              onBack={() => select(null)}
              onTaskReplaced={upsertTask}
            />
          ) : (
            <div className="muted centered">Select a task (or press j/k).</div>
          )}
        </section>
      </div>

      <CommandBar open={showHelp} onClose={() => setShowHelp(false)} />

      <NewTaskModal
        open={showNewTask}
        defaultTeamId={null}
        onClose={() => setShowNewTask(false)}
        onCreate={createTask}
      />

      {ctx.contextMenu && (
        <ContextMenu
          x={ctx.contextMenu.x}
          y={ctx.contextMenu.y}
          items={ctx.buildMenuItems(ctx.contextMenu.task, upsertTask, removeLocalTask, deleteWithUndo)}
          onClose={ctx.closeContextMenu}
        />
      )}

      <MoveScopeDialog
        open={!!ctx.moveDialogFor}
        currentTeamId={ctx.moveDialogFor?.teamId ?? null}
        onSelect={(toTeamId) => {
          if (ctx.moveDialogFor) {
            void ctx.onMoveScope(ctx.moveDialogFor, toTeamId, upsertTask, removeLocalTask);
          }
        }}
        onClose={ctx.closeMoveDialog}
      />
    </div>
  );
}
